import React from 'react';
import moment from 'moment';
import {
  Table,
  DatePicker,
} from 'antd';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import BorderlessButton from '../common/buttons/BorderlessButton';
import OnTraccrNumberInput from '../common/inputs/OnTraccrNumberInput';
import colors from '../constants/Colors';
import MaterialUnitsSelector from '../common/inputs/MaterialUnitsSelector';
import { currencyFormatter, currencyParser } from '../helpers/inputParsers';
import { sortByCode } from '../helpers/helpers';
import OnTraccrTextInput from '../common/inputs/OnTraccrTextInput';
import { getMaterialCostColumns, getEquipmentCostColumns, getBasicCostColumns } from '../projects/ProjectProgress/ProgressColumns';
import OnTraccrCheckbox from '../common/inputs/OnTraccrCheckbox';
import { EQUIPMENT_CATEGORY_ID, getCategoryAssignmentValue, MATERIAL_CATEGORY_ID } from '../helpers/costcodeHelpers';
import TagRender from '../common/TagRender';

const { RangePicker } = DatePicker;

const formatDates = ({ startDate, endDate }) => {
  if (!startDate || !endDate) return '';
  return `${moment(startDate).format('YYYY-MM-DD')} -> ${moment(endDate).format('YYYY-MM-DD')}`;
};

const getExpandedRowRender = (record) => {
  const costcodeCostEstimates = record.costUpdates ?? [];
  let getColumns = null;

  const { categoryIds = [] } = record ?? {};

  if (categoryIds.includes(MATERIAL_CATEGORY_ID)) {
    getColumns = getMaterialCostColumns;
  } else if (categoryIds.includes(EQUIPMENT_CATEGORY_ID)) {
    getColumns = getEquipmentCostColumns;
  } else {
    getColumns = getBasicCostColumns;
  }

  return (
    <Table
      columns={getColumns()}
      dataSource={costcodeCostEstimates}
      pagination={false}
      size="small"
      style={{ marginBottom: 10 }}
      rowKey="id"
    />
  );
};

class CostCodeUploadList extends React.Component {
  constructor(props) {
    super(props);
    const { defaultSelected } = props;
    this.state = {
      selectedRowKeys: defaultSelected,
      sortedDataSource: [],
    };

    this.onPhasedChanged = this.phasedChanged.bind(this);

    this.codeMap = {};
    this.nameMap = {};
  }

  componentDidMount() {
    const {
      dataSource = [],
      shouldSort = true,
    } = this.props;
    this.checkDuplicates();

    let newData = [...dataSource];
    if (shouldSort) {
      newData.sort(sortByCode('code'));
    }

    this.setState((prevState) => ({
      ...prevState,
      sortedDataSource: newData,
    }));
  }

  componentDidUpdate(prevProps) {
    const {
      dataSource: prevData = [],
    } = prevProps;
    const {
      dataSource = [],
      shouldSort = true,
    } = this.props;
    if (prevData !== dataSource) {
      this.checkDuplicates();

      let newData = [...dataSource];
      if (shouldSort) {
        newData.sort(sortByCode('code'));
      }

      this.setState((prevState) => ({
        ...prevState,
        sortedDataSource: newData,
      }));
    }
  }

  getInputColumn({
    conflictMap = {},
    record = {},
    value,
    key,
  }) {
    const { onEdit } = this.props;
    const lookupKey = value ? String(value).trim().toLowerCase() : (value ?? '');
    const hasConflict = conflictMap[lookupKey] > 1;
    if (onEdit) {
      return (
        <OnTraccrTextInput
          value={value}
          onChange={(e) => {
            const newValue = e?.target?.value;
            onEdit(record.id, { [key]: newValue });
          }}
          style={
            !value || hasConflict
              ? {
                borderColor: 'red',
                textColor: 'red',
              }
              : {}
          }
        />
      );
    }
    return (
      <div
        style={{
          color: hasConflict ? 'red' : 'inherit',
          paddingLeft: 10,
        }}
      >
        {value}
      </div>
    );
  }

  checkDuplicates() {
    const {
      dataSource = [],
      conflictList,
    } = this.props;

    this.codeMap = {};
    this.nameMap = {};

    const conflictCheck = conflictList ?? dataSource;

    dataSource?.forEach((data) => {
      const trimmedName = data.name ? String(data.name).trim().toLowerCase() : data.name;
      if (trimmedName) {
        this.nameMap[trimmedName] = (this.nameMap[trimmedName] || 0) + 1;
      }
    });
    // Code is unique across categories
    conflictCheck?.forEach((data) => {
      const trimmedCode = data.code ? String(data.code).trim().toLowerCase() : data.code;
      if (trimmedCode) {
        this.codeMap[trimmedCode] = (this.codeMap[trimmedCode] || 0) + 1;
      }
    });

  }

  phasedChanged(record, checked) {
    const { onPhaseChanged } = this.props;
    onPhaseChanged(record, checked);
  }

  render() {
    const {
      selectable = false,
      categories = false,
      phased = false,
      phaseClickable = true,
      onRemove,
      style = {},
      scroll = {},
      defaultSelected = [],
      onEstimateChanged,
      hours,
      days,
      cost,
      quantity,
      units,
      dates,
      onSelectedChanged,
      showCostEstimates = false,
      showSingleCategory = false,
      settings,
      categoryId,
      categoriesIdMap,
    } = this.props;

    const {
      selectedRowKeys,
      sortedDataSource,
    } = this.state;

    const cols = [
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        render: (name, record) => (
          this.getInputColumn({
            conflictMap: this.nameMap,
            record,
            value: name,
            key: 'name',
          })
        ),
      },
      {
        title: 'Code',
        dataIndex: 'code',
        key: 'code',
        render: (code, record) => (
          this.getInputColumn({
            conflictMap: this.codeMap,
            record,
            value: code,
            key: 'code',
          })
        ),
      },
      {
        title: 'Description',
        dataIndex: 'description',
        key: 'description',
        render: (description, record) => (
          this.getInputColumn({
            record,
            value: description,
            key: 'description',
          })
        ),
      },
    ];

    const defaultCheckSet = new Set(defaultSelected);
    const tableProps = {};
    if (selectable) {
      tableProps.rowSelection = {
        onChange: (newSelectedKeys, selectedRows) => {
          this.setState({ selectedRowKeys: newSelectedKeys });
          onSelectedChanged(selectedRows);
        },
        getCheckboxProps: (record) => ({
          name: record.name,
          defaultChecked: defaultCheckSet.has(record.id),
          className: settings?.settings?.uxRedesignPt1 ? 'ontraccr-checkbox' : '',
        }),
        selectedRowKeys,
      };
      tableProps.onHeaderRow = () => {
        if (!settings?.settings?.uxRedesignPt1) return null;
        return {
          className: 'ontraccr-table-header',
        };
      };
    }

    if (showCostEstimates) {
      tableProps.expandable = {
        defaultExpandAllRows: false,
        expandedRowRender: getExpandedRowRender,
        rowExpandable: (record) => record?.costUpdates?.length,
      };
    }

    if (categories) {
      cols.push({
        title: showSingleCategory ? 'Category' : 'Categories',
        dataIndex: 'categoryIds',
        key: 'categoryIds',
        render: (_, record) => {
          const { categoryIds = [] } = record ?? {};
          const tagsToShow = showSingleCategory && categoryId ? [categoryId] : categoryIds;

          return (
            <TagRender
              tagMap={categoriesIdMap}
              tagIds={tagsToShow}
              useRedesign
            />
          );
        },
      });
    }

    if (phased) {
      cols.push({
        title: 'Phased',
        dataIndex: 'phased',
        key: 'phased',
        align: 'center',
        render: (_, record) => (
          <OnTraccrCheckbox
            style={{ display: 'block' }}
            onChange={(e) => this.onPhasedChanged(record, e)}
            value={record.phased}
            disabled={!phaseClickable}
          />
        ),
      });
    }

    if (hours) {
      cols.push({
        title: 'Hours',
        dataIndex: 'hours',
        key: 'hours',
        render: (_, record) => {
          const categoryHours = getCategoryAssignmentValue(record, categoryId, 'hours');

          if (!onEstimateChanged) return categoryHours;

          return (
            <OnTraccrNumberInput
              min={0}
              value={categoryHours}
              onChange={(value) => {
                const parsed = parseFloat(value);
                const finalValue = Number.isNaN(parsed) || !parsed
                  ? 0
                  : parsed;
                const updateObj = {
                  categoryAssignments: {
                    [categoryId]: {
                      hours: finalValue,
                    },
                  },
                };
                onEstimateChanged(record, updateObj);
              }}
              style={{ width: 100 }}
            />
          );
        },
      });
    }

    if (days) {
      cols.push({
        title: 'Days',
        dataIndex: 'days',
        key: 'days',
        render: (_, record) => {
          const categoryDays = getCategoryAssignmentValue(record, categoryId, 'days');

          if (!onEstimateChanged) return categoryDays;

          return (
            <OnTraccrNumberInput
              min={0}
              value={categoryDays}
              onChange={(value) => {
                const parsed = parseFloat(value);
                const finalValue = Number.isNaN(parsed) || !parsed
                  ? 0
                  : parsed;
                const updateObj = {
                  categoryAssignments: {
                    [categoryId]: {
                      days: finalValue,
                    },
                  },
                };
                onEstimateChanged(record, updateObj);
              }}
              style={{ width: 100 }}
            />
          );
        },
      });
    }

    if (cost) {
      cols.push({
        title: 'Cost',
        dataIndex: 'estimatedCost',
        key: 'estimatedCost',
        render: (_, record) => {
          const categoryEstimatedCost = getCategoryAssignmentValue(record, categoryId, 'estimatedCost');

          if (!onEstimateChanged) return currencyFormatter(categoryEstimatedCost);

          return (
            <OnTraccrNumberInput
              min={0}
              value={categoryEstimatedCost}
              onChange={(value) => {
                const parsed = parseFloat(value);
                const finalValue = Number.isNaN(parsed) || !parsed
                  ? 0
                  : parsed;
                const updateObj = {
                  categoryAssignments: {
                    [categoryId]: {
                      estimatedCost: finalValue,
                    },
                  },
                };
                onEstimateChanged(record, updateObj);
              }}
              style={{ width: 100 }}
              formatter={currencyFormatter}
              parser={currencyParser}
            />
          );
        },
      });
    }

    if (quantity) {
      cols.push({
        title: 'Estimated Quantity',
        dataIndex: 'estimatedQuantity',
        key: 'estimatedQuantity',
        render: (_, record) => {
          const categoryEstimatedQuantity = getCategoryAssignmentValue(record, categoryId, 'estimatedQuantity');

          if (!onEstimateChanged) return categoryEstimatedQuantity;

          if (!(categoryEstimatedQuantity || record.addQuantities)) {
            return {
              children: (
                <BorderlessButton
                  title="Add Quantities"
                  style={{ width: 150 }}
                  onClick={() => onEstimateChanged(record, { addQuantities: true })}
                />
              ),
              props: { colSpan: 1 },
            };
          }

          return (
            <OnTraccrNumberInput
              min={0}
              value={categoryEstimatedQuantity}
              onChange={(value) => {
                const parsed = parseFloat(value);
                const finalValue = Number.isNaN(parsed) || !parsed
                  ? 0
                  : parsed;
                const updateObj = {
                  categoryAssignments: {
                    [categoryId]: {
                      estimatedQuantity: finalValue,
                    },
                  },
                };
                onEstimateChanged(record, updateObj);
              }}
              style={{ width: 100 }}
            />
          );
        },
      });
    }

    if (units) {
      cols.push({
        title: 'Units',
        dataIndex: 'units',
        key: 'units',
        render: (_, record) => {
          const categoryUnits = getCategoryAssignmentValue(record, categoryId, 'units');
          if (!(categoryUnits || record.addQuantities)) return null;
          if (!onEstimateChanged) return categoryUnits;
          return (
            <MaterialUnitsSelector
              value={categoryUnits}
              onSelect={(newUnits) => {
                const updateObj = {
                  categoryAssignments: {
                    [categoryId]: {
                      units: newUnits,
                    },
                  },
                };
                onEstimateChanged(record, updateObj);
              }}
            />
          );
        },
      });
    }

    // Add Daily Cost and Hourly Cost columns for Equipment
    const hasEquipmentCategory = sortedDataSource.some((record) => {
      const { categoryAssignments = {} } = record ?? {};
      return EQUIPMENT_CATEGORY_ID in categoryAssignments;
    });
    if (onEstimateChanged && hasEquipmentCategory) {
      cols.push({
        title: 'Daily Cost',
        dataIndex: 'dailyWage',
        key: 'dailyWage',
        render: (_, record) => {
          const { categoryAssignments = {} } = record ?? {};
          const hasEquipment = EQUIPMENT_CATEGORY_ID in categoryAssignments;
          if (hasEquipment) {
            return (
              <OnTraccrNumberInput
                min={0}
                value={record.dailyWage}
                onChange={(value) => {
                  const parsed = parseFloat(value);
                  onEstimateChanged(record, Number.isNaN(parsed) || !parsed
                    ? { dailyWage: 0 }
                    : { dailyWage: parsed });
                }}
                style={{ width: 120 }}
                formatter={currencyFormatter}
                parser={currencyParser}
              />
            );
          }
          return record.dailyWage ? currencyFormatter(record.dailyWage) : '-';
        },
      });

      cols.push({
        title: 'Hourly Cost',
        dataIndex: 'hourlyWage',
        key: 'hourlyWage',
        render: (_, record) => {
          const { categoryAssignments = {} } = record ?? {};
          const hasEquipment = EQUIPMENT_CATEGORY_ID in categoryAssignments;
          if (hasEquipment) {
            return (
              <OnTraccrNumberInput
                min={0}
                value={record.hourlyWage}
                onChange={(value) => {
                  const parsed = parseFloat(value);
                  onEstimateChanged(record, Number.isNaN(parsed) || !parsed
                    ? { hourlyWage: 0 }
                    : { hourlyWage: parsed });
                }}
                style={{ width: 120 }}
                formatter={currencyFormatter}
                parser={currencyParser}
              />
            );
          }
          return record.hourlyWage ? currencyFormatter(record.hourlyWage) : '-';
        },
      });
    }

    if (dates) {
      cols.push({
        title: 'Dates',
        dataIndex: 'dates',
        key: 'dates',
        render: (_, record) => (
          onEstimateChanged
            ? (
              <RangePicker
                style={{ width: 300 }}
                onChange={onEstimateChanged ? ([start, end] = []) => onEstimateChanged(record, {
                  startDate: start,
                  endDate: end,
                }) : null}
                defaultValue={(
                  record.startDate && record.endDate
                    ? [moment(record.startDate), moment(record.endDate)]
                    : null
                )}
              />
            )
            : formatDates(record)
        ),
      });
    }

    if (onRemove) {
      cols.push({
        title: 'Remove',
        dataIndex: 'remove',
        key: 'remove',
        align: 'center',
        render: (_, record) => (
          <BorderlessButton
            icon="delete"
            color={colors.ONTRACCR_RED}
            onClick={() => onRemove(record)}
            style={{ paddingRight: 8, paddingLeft: 0 }}
          />
        ),
      });
    }

    return (
      <Table
        size="small"
        columns={cols}
        dataSource={sortedDataSource.map((item) => ({
          ...item,
          key: item.id,
        }))}
        pagination={false}
        style={{ marginBottom: 50, ...style }}
        scroll={scroll}
        {...tableProps}
      />
    );
  }
}

CostCodeUploadList.propTypes = {
  onEdit: PropTypes.func,
  days: PropTypes.bool,
  showSingleCategory: PropTypes.bool,
};

CostCodeUploadList.defaultProps = {
  onEdit: null,
  days: false,
  showSingleCategory: false,
};

export default connect((state, ownProps) => ({
  settings: state.settings.company,
  categoriesIdMap: state.costcodes.categoriesIdMap,
  ...ownProps,
}))(CostCodeUploadList);
